import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { MuiChipsInput } from 'mui-chips-input'
import authService from '../api-authorization/AuthorizeService'
import StyledActionButton from "../GreenButton";
import { useEffect } from "react";

export default function FormDialog({ cb, brand, openEditMode, onClose }) {
    const [items, setItems] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [projectName, setProjectName] = React.useState("");
    const [chips, setChips] = React.useState([]);

    // ✅ NEW: other names field (maps to JSON "alternate_names")
    const [otherNames, setOtherNames] = React.useState("");

    useEffect(() => {
        if (brand) {
            setProjectName(brand.name || "");
            setChips(Array.isArray(brand.domains) ? brand.domains.map(x => x.domain) : []);

            // brand.otherNames might come back as "otherNames" or "alternate_names" depending on your API serializer
            // Prefer whatever exists.
            setOtherNames(brand.otherNames ?? brand.alternate_names ?? "");
        } else {
            // creating new brand
            setProjectName("");
            setChips([]);
            setOtherNames("");
        }

        if (openEditMode) {
            setOpen(openEditMode);
        }
    }, [brand, openEditMode]);

    const handleClickOpen = async () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        if (onClose) onClose();
    };

    const handleChange = (newChips) => setChips(newChips || []);
    const handleNameChange = (e) => setProjectName(e.target.value);
    const handleOtherNamesChange = (e) => setOtherNames(e.target.value);

    const create = async () => {
        const req = {
            name: projectName,
            domains: chips,

            // ✅ NEW: send as "alternate_names" to match [JsonProperty("alternate_names")]
            alternate_names: otherNames || ""
        };

        if (openEditMode) {
            const response = await fetch('/api/organisation/brands/' + brand.id, {
                method: "PUT",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(req)
            });
            authService.followIfLoginRedirect(response);

            if (onClose) onClose();
            setOpen(false);
        } else {
            const response = await fetch('/api/organisation/brands', {
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(req)
            });
            authService.followIfLoginRedirect(response);
            await response.json();

            setOpen(false);
            setChips([]);
            setProjectName("");
            setOtherNames("");
            if (cb) cb();
        }
    };

    return (
        <div>
            {!brand && (
                <StyledActionButton onClick={handleClickOpen}>
                    Create
                </StyledActionButton>
            )}

            <Dialog open={(openEditMode || open)} onClose={handleClose}>
                <DialogTitle>{openEditMode ? "Edit Brand" : "Create Brand"}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Create a brand and add domains + alternate names for matching.
                    </DialogContentText>

                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Brand Name"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={projectName}
                        onChange={handleNameChange}
                    />

                    {/* ✅ NEW: Other names field */}
                    <TextField
                        margin="dense"
                        id="alternate_names"
                        label="Alternate names"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={otherNames}
                        onChange={handleOtherNamesChange}
                        helperText="Comma-separated or free text (e.g. brand aliases, common misspellings)."
                    />

                    <DialogContentText className="mt-3">
                        Enter domains for this brand
                    </DialogContentText>

                    <MuiChipsInput
                        margin="dense"
                        label="Domains"
                        variant="standard"
                        fullWidth={true}
                        value={chips}
                        onChange={handleChange}
                        validate={(chipValue) => {
                            return {
                                isError:
                                    chipValue.length <= 3 ||
                                    !(/^[.a-zA-Z0-9-]{1,61}(\.[a-zA-Z]{2,}){1,}$/.test(chipValue)),
                                textError: 'the value must be at least 3 characters long'
                            };
                        }}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={create}>
                        {openEditMode ? "Save Changes" : "Create New"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
